import { useState, useEffect } from 'react'
import { Plus, Search, Edit2, Trash2, Phone, Mail, User as UserIcon, Link, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { customerApi, CustomerCreateRequest } from '../../api/customers'
import { tenantApi } from '../../api'
import { Customer, CustomerStatus, Tenant } from '../../types'
import { useAuthStore } from '../../stores/authStore'
import TableLoading from '@/components/ui/TableLoading'
import CustomerDrawer from '@/components/customers/CustomerDrawer'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

export default function CustomersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | ''>('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [error, setError] = useState<string>('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopyBookingUrl = (customer: Customer) => {
    if (!customer.booking_token) return
    const url = `${window.location.origin}/booking/${customer.booking_token}`
    navigator.clipboard.writeText(url)
    setCopiedId(customer.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await customerApi.getCustomers(
        page,
        limit,
        statusFilter || undefined,
        search
      )
      setCustomers(data.items)
      setTotal(data.total)
    } catch (error: any) {
      console.error('Failed to fetch customers:', error)
      if (error.response?.status === 401) {
        navigate('/login', { replace: true })
      } else {
        setError('載入客戶資料失敗，請稍後再試')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchTenants = async () => {
    try {
      const data = await tenantApi.list({ limit: 100 })
      setTenants(data.items)
    } catch (error) {
      console.error('Failed to fetch tenants:', error)
    }
  }

  const handleOpenDrawer = async () => {
    await fetchTenants()
    setIsDrawerOpen(true)
  }

  // 檢查認證並加載數據
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    fetchCustomers()
  }, [user, page, limit, statusFilter, search, navigate])

  const handleEdit = async (customer: Customer) => {
    setSelectedCustomer(customer)
    await fetchTenants()
    setIsDrawerOpen(true)
  }

  const handleDelete = async (id: string) => {
    setShowDeleteConfirm(id)
  }

  const confirmDelete = async (id: string) => {
    try {
      await customerApi.deleteCustomer(id)
      setShowDeleteConfirm(null)
      fetchCustomers()
    } catch (error) {
      console.error('Failed to delete customer:', error)
    }
  }

  const handleCreate = async (data: CustomerCreateRequest & { tenant_id?: string }) => {
    try {
      await customerApi.createCustomer(data)
      setIsDrawerOpen(false)
      fetchCustomers()
    } catch (error: any) {
      console.error('Failed to create customer:', error)
      alert(error?.response?.data?.detail || '新增客戶失敗，請稍後再試')
      throw error
    }
  }

  const handleUpdate = async (data: any) => {
    try {
      if (selectedCustomer) {
        await customerApi.updateCustomer(selectedCustomer.id, data)
      }
      setIsDrawerOpen(false)
      setSelectedCustomer(null)
      fetchCustomers()
    } catch (error: any) {
      console.error('Failed to update customer:', error)
      alert(error?.response?.data?.detail || '更新客戶失敗，請稍後再試')
      throw error
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="space-y-6 p-1.5 sm:p-6">
      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-red-900 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">客戶管理</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">管理客戶資訊與訂單記錄</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="搜尋姓名、電話或 Email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CustomerStatus | '')}
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm focus:ring-1 focus:ring-ring"
        >
          <option value="">所有狀態</option>
          <option value={CustomerStatus.ACTIVE}>啟用</option>
          <option value={CustomerStatus.INACTIVE}>停用</option>
          <option value={CustomerStatus.BLACKLISTED}>黑名單</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">正在加載客戶...</div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">找不到符合條件的客戶</div>
          ) : (
            customers.map((customer) => (
              <div key={customer.id} className="p-4 space-y-3 bg-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                      <UserIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-bold text-foreground">{customer.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{customer.address || '無地址'}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    customer.status === CustomerStatus.ACTIVE
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      : customer.status === CustomerStatus.INACTIVE
                      ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                  }`}>
                    {customer.status === CustomerStatus.ACTIVE ? '啟用' : 
                     customer.status === CustomerStatus.INACTIVE ? '停用' : '黑名單'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 col-span-2">
                    <Phone className="h-4 w-4" />
                    <span className="text-foreground">{customer.phone}</span>
                  </div>
                  {customer.tax_id && (
                    <div className="col-span-2 text-xs">
                      統編: <span className="text-foreground font-medium">{customer.tax_id}</span>
                    </div>
                  )}
                  <div className="text-xs">
                    訂單數: <span className="font-medium text-foreground">{customer.total_orders}</span>
                  </div>
                  <div className="text-xs">
                    加入: {format(new Date(customer.created_at), 'yyyy/MM/dd')}
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(customer)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" /> 編輯
                  </Button>
                  {customer.booking_token && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={() => handleCopyBookingUrl(customer)}
                    >
                      {copiedId === customer.id ? <Check className="w-4 h-4 mr-1" /> : <Link className="w-4 h-4 mr-1" />}
                      {copiedId === customer.id ? '已複製' : '訂車連結'}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(customer.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> 刪除
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-6 py-3 font-medium">客戶資訊</th>
                <th className="px-6 py-3 font-medium">聯絡方式</th>
                <th className="px-6 py-3 font-medium">狀態</th>
                <th className="px-6 py-3 font-medium">訂單數</th>
                <th className="px-6 py-3 font-medium">加入時間</th>
                <th className="px-6 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <TableLoading colSpan={6} message="正在加載客戶..." />
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <UserIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      找不到符合條件的客戶
                    </p>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                          <UserIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground">{customer.name}</div>
                          <div className="text-xs text-muted-foreground">{customer.address || '無地址'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3" />
                          <span className="text-foreground">{customer.phone}</span>
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                        {customer.tax_id && (
                          <div className="text-xs text-muted-foreground">
                            統編: <span className="text-foreground font-medium">{customer.tax_id}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        customer.status === CustomerStatus.ACTIVE
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : customer.status === CustomerStatus.INACTIVE
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {customer.status === CustomerStatus.ACTIVE ? '啟用' : 
                         customer.status === CustomerStatus.INACTIVE ? '停用' : '黑名單'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-foreground">{customer.total_orders}</td>
                    <td className="px-6 py-4 text-foreground">{format(new Date(customer.created_at), 'yyyy/MM/dd')}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(customer)}
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="編輯"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {customer.booking_token && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyBookingUrl(customer)}
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                            title="複製專屬訂車連結"
                          >
                            {copiedId === customer.id ? <Check className="h-4 w-4" /> : <Link className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer.id)}
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="刪除"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              上一頁
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={customers.length < limit}
            >
              下一頁
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                顯示第 <span className="font-medium">{(page - 1) * limit + 1}</span> 到 <span className="font-medium">{Math.min(page * limit, total)}</span> 筆，共 <span className="font-medium">{total}</span> 筆
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  variant="outline"
                  className="rounded-l-md rounded-r-none px-2 py-2"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <span className="sr-only">Previous</span>
                  上一頁
                </Button>
                <Button
                  variant="outline"
                  className="rounded-r-md rounded-l-none px-2 py-2"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= total}
                >
                  <span className="sr-only">Next</span>
                  下一頁
                </Button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <CustomerDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelectedCustomer(null)
        }}
        onSubmit={selectedCustomer ? handleUpdate : handleCreate}
        tenants={tenants}
        customer={selectedCustomer}
      />

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">確認刪除</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">確定要刪除此客戶嗎？此操作無法撤銷。</p>
            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2"
              >
                取消
              </Button>
              <Button
                onClick={() => confirmDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                刪除
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        onClick={handleOpenDrawer}
        className="fixed z-40 bottom-20 right-4 lg:bottom-10 lg:right-10 h-14 w-14 rounded-full shadow-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        <Plus className="h-8 w-8" />
      </Button>
    </div>
  )
}
